import { useState } from 'react';
import {
  ChartLine, ChatDots, Users, Package, BookOpen, FileText,
  ChartBar, Briefcase, Trophy, IdentificationBadge, MapPinArea, Gear,
  Phone, MapPin, Receipt, CurrencyDollar, Target, Flame, Bell,
  WhatsappLogo, MessengerLogo, ArrowsClockwise, Check
} from '@phosphor-icons/react';

// Two palettes side-by-side: Terracotta (new proposal) and Refined Blue (current evolution)
const PALETTES = {
  terracotta: {
    name: 'Terracota & Hueso',
    light: {
      bg: '#F9F8F6', surface: '#FFFFFF', surfaceMuted: '#F3F1EC',
      primary: '#C5533B', primaryHover: '#A6432D',
      text: '#1A1A1A', textMuted: '#6B6B66', border: '#E5E3DB',
      success: '#4A7C59', warning: '#E27D60', accent: '#F2A900',
    },
    dark: {
      bg: '#1A1C1E', surface: '#242628', surfaceMuted: '#2E3033',
      primary: '#D6664D', primaryHover: '#F07B60',
      text: '#F3F1EC', textMuted: '#9A9A9A', border: '#3A3C3E',
      success: '#5C996E', warning: '#F48C6B', accent: '#FFB822',
    },
  },
  blueRefined: {
    name: 'Azul Refinado (evolución)',
    light: {
      bg: '#F7F8FA', surface: '#FFFFFF', surfaceMuted: '#F1F3F7',
      primary: '#1D4ED8', primaryHover: '#1E40AF',
      text: '#0F172A', textMuted: '#64748B', border: '#E2E8F0',
      success: '#16A34A', warning: '#F59E0B', accent: '#0EA5E9',
    },
    dark: {
      bg: '#0F172A', surface: '#111827', surfaceMuted: '#1E293B',
      primary: '#3B82F6', primaryHover: '#2563EB',
      text: '#F1F5F9', textMuted: '#94A3B8', border: '#1F2937',
      success: '#22C55E', warning: '#F59E0B', accent: '#38BDF8',
    },
  },
};

const FONTS = {
  cabinet: { name: 'Cabinet Grotesk', heading: '"Cabinet Grotesk", sans-serif', body: '"Manrope", sans-serif' },
  outfit: { name: 'Outfit (más estándar)', heading: '"Outfit", sans-serif', body: '"Manrope", sans-serif' },
};

const DesignPreview = () => {
  const [paletteKey, setPaletteKey] = useState('terracotta');
  const [mode, setMode] = useState('light');
  const [fontKey, setFontKey] = useState('cabinet');

  const c = PALETTES[paletteKey][mode];
  const font = FONTS[fontKey];

  return (
    <div
      style={{ background: c.bg, color: c.text, fontFamily: font.body, minHeight: '100vh' }}
      className="transition-colors duration-300"
      data-testid="design-preview-page"
    >
      {/* Control bar */}
      <div
        style={{ background: c.surface, borderColor: c.border }}
        className="border-b sticky top-0 z-50 backdrop-blur-xl px-6 py-3 flex flex-wrap items-center gap-3 justify-between"
      >
        <div className="flex items-center gap-3">
          <span style={{ fontFamily: font.heading, fontWeight: 800, fontSize: 18, color: c.primary }}>SACH</span>
          <span style={{ color: c.textMuted }} className="text-xs font-bold uppercase tracking-[0.2em]">Design Preview</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {/* Palette toggle */}
          <div style={{ background: c.surfaceMuted, borderColor: c.border }} className="border rounded-full p-1 flex">
            {Object.entries(PALETTES).map(([k, p]) => (
              <button
                key={k}
                onClick={() => setPaletteKey(k)}
                style={{
                  background: paletteKey === k ? c.primary : 'transparent',
                  color: paletteKey === k ? '#fff' : c.text,
                }}
                className="px-3 py-1.5 rounded-full font-bold text-[11px] transition-colors"
                data-testid={`palette-${k}`}
              >
                {p.name}
              </button>
            ))}
          </div>
          {/* Mode toggle */}
          <div style={{ background: c.surfaceMuted, borderColor: c.border }} className="border rounded-full p-1 flex">
            {['light', 'dark'].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  background: mode === m ? c.text : 'transparent',
                  color: mode === m ? c.bg : c.text,
                }}
                className="px-3 py-1.5 rounded-full font-bold text-[11px]"
                data-testid={`mode-${m}`}
              >
                {m === 'light' ? '☀️ Claro' : '🌑 Oscuro'}
              </button>
            ))}
          </div>
          {/* Font toggle */}
          <div style={{ background: c.surfaceMuted, borderColor: c.border }} className="border rounded-full p-1 flex">
            {Object.entries(FONTS).map(([k, f]) => (
              <button
                key={k}
                onClick={() => setFontKey(k)}
                style={{
                  background: fontKey === k ? c.text : 'transparent',
                  color: fontKey === k ? c.bg : c.text,
                  fontFamily: f.heading,
                }}
                className="px-3 py-1.5 rounded-full font-bold text-[11px]"
                data-testid={`font-${k}`}
              >
                {f.name.split(' ')[0]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto p-6 md:p-10 space-y-12">
        {/* Hero */}
        <section>
          <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.25em] font-bold mb-3">Vista previa</p>
          <h1 style={{ fontFamily: font.heading, fontWeight: 800, color: c.text }} className="text-5xl md:text-6xl tracking-tight leading-[1.05]">
            Así se vería tu CRM 🎨
          </h1>
          <p style={{ color: c.textMuted }} className="mt-4 text-base max-w-2xl">
            Cambia paleta, modo claro/oscuro y tipografía arriba ⬆ para comparar.
            <br />Todo abajo cambia en vivo.
          </p>
        </section>

        {/* Color swatches */}
        <section>
          <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Paleta activa</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {[
              { name: 'Fondo', value: c.bg },
              { name: 'Superficie', value: c.surface },
              { name: 'Primario', value: c.primary },
              { name: 'Éxito', value: c.success },
              { name: 'Alerta', value: c.warning },
              { name: 'Acento', value: c.accent },
            ].map((s) => (
              <div key={s.name} style={{ background: c.surface, borderColor: c.border }} className="border rounded-[12px] p-3">
                <div style={{ background: s.value }} className="h-12 rounded-md mb-2 ring-1 ring-black/5" />
                <p style={{ color: c.text }} className="text-xs font-bold">{s.name}</p>
                <p style={{ color: c.textMuted }} className="text-[10px]">{s.value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Sidebar preview */}
        <section className="grid lg:grid-cols-[280px_1fr] gap-6">
          <div style={{ background: c.surface, borderColor: c.border }} className="border rounded-[16px] p-4">
            <p style={{ color: c.textMuted }} className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Negocio</p>
            <div style={{ background: c.surfaceMuted, borderColor: c.border }} className="border rounded-[10px] px-3 py-2 mb-4 flex items-center justify-between">
              <span style={{ fontFamily: font.heading, fontWeight: 700, color: c.text }} className="text-sm">Sach Productos Yale</span>
              <ArrowsClockwise size={14} color={c.textMuted} />
            </div>

            <SidebarGroup label="OPERACIÓN" c={c} font={font}>
              <NavItem icon={ChartLine} label="Dashboard" c={c} />
              <NavItem icon={Briefcase} label="Mi Día" c={c} badge="🔥 5" active />
              <NavItem icon={ChatDots} label="Mensajes" c={c} badge="13" />
              <NavItem icon={Package} label="Pedidos" c={c} />
            </SidebarGroup>
            <SidebarGroup label="ANÁLISIS" c={c} font={font}>
              <NavItem icon={ChartBar} label="Reportes" c={c} />
              <NavItem icon={Target} label="Plan de Trabajo" c={c} />
              <NavItem icon={Trophy} label="Top Vendedores" c={c} />
              <NavItem icon={MapPinArea} label="Cobertura" c={c} />
            </SidebarGroup>
            <SidebarGroup label="ADMINISTRACIÓN" c={c} font={font}>
              <NavItem icon={Users} label="Clientes" c={c} />
              <NavItem icon={BookOpen} label="Catálogo" c={c} />
              <NavItem icon={IdentificationBadge} label="Vendedores" c={c} />
            </SidebarGroup>
            <SidebarGroup label="CONFIGURACIÓN" c={c} font={font}>
              <NavItem icon={FileText} label="Plantillas" c={c} />
              <NavItem icon={Gear} label="Configuración" c={c} />
            </SidebarGroup>
          </div>

          {/* Main column samples */}
          <div className="space-y-6">
            {/* KPI cards */}
            <div>
              <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Dashboard / KPI cards</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <KpiCard c={c} font={font} label="Ventas del mes" value="Q 187,450" trend="+12.4%" trendUp />
                <KpiCard c={c} font={font} label="Cotizaciones" value="34" trend="+5" trendUp accent />
                <KpiCard c={c} font={font} label="Tasa de cierre" value="42%" trend="-3%" />
              </div>
            </div>

            {/* Mi Día quick actions */}
            <div>
              <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Mi Día / Botones rápidos</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <QuickAction c={c} font={font} icon={Phone} label="Llamada" count={3} color={c.primary} />
                <QuickAction c={c} font={font} icon={MapPin} label="Visita" count={1} color={c.warning} />
                <QuickAction c={c} font={font} icon={Receipt} label="Cotización" count={2} color={c.accent} />
                <QuickAction c={c} font={font} icon={CurrencyDollar} label="Venta" count={1} color={c.success} highlight />
              </div>
            </div>

            {/* Goal progress + streak */}
            <div style={{ background: c.surface, borderColor: c.border }} className="border rounded-[16px] p-5">
              <div className="flex items-center gap-3 mb-3">
                <div style={{ background: `${c.accent}22`, color: c.accent }} className="w-10 h-10 rounded-full flex items-center justify-center">
                  <Flame size={20} weight="fill" />
                </div>
                <div className="flex-1">
                  <p style={{ color: c.textMuted }} className="text-[10px] uppercase tracking-[0.2em] font-bold">Racha de actividad</p>
                  <p style={{ fontFamily: font.heading, color: c.text }} className="text-2xl font-extrabold tracking-tight">5 días seguidos 🔥</p>
                </div>
                <span style={{ background: c.success, color: '#fff' }} className="text-xs font-bold px-3 py-1 rounded-full">Meta hoy ✓</span>
              </div>
              <div style={{ background: c.surfaceMuted }} className="h-3 rounded-full overflow-hidden">
                <div style={{ background: `linear-gradient(90deg, ${c.warning}, ${c.success})`, width: '74%' }} className="h-full transition-all" />
              </div>
              <div className="flex justify-between mt-2">
                <p style={{ color: c.textMuted }} className="text-xs">Q 37,250 / Q 50,000</p>
                <p style={{ color: c.success }} className="text-xs font-bold">74%</p>
              </div>
            </div>

            {/* Insight card */}
            <div
              style={{
                background: `linear-gradient(135deg, ${c.primary}10, ${c.accent}15)`,
                borderColor: `${c.primary}30`,
              }}
              className="border rounded-[16px] p-5"
            >
              <div className="flex items-center gap-3">
                <Trophy size={28} weight="fill" color={c.primary} />
                <div>
                  <p style={{ color: c.textMuted }} className="text-[10px] uppercase tracking-[0.2em] font-bold">Insight</p>
                  <p style={{ fontFamily: font.heading, color: c.text }} className="text-xl font-extrabold">Quetzaltenango es tu mejor sucursal este mes</p>
                  <p style={{ color: c.textMuted }} className="text-xs mt-1">+38% vs el mes pasado · Jackson lidera con Q 24,800</p>
                </div>
              </div>
            </div>

            {/* Chat preview */}
            <div>
              <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Inbox / Burbujas de chat</p>
              <div style={{ background: c.surface, borderColor: c.border }} className="border rounded-[16px] p-4 space-y-2">
                <ChatHeader c={c} font={font} />
                <div className="flex flex-col gap-2 mt-3">
                  <ChatBubble c={c} font={font} side="them" text="Hola, busco bisagras para puerta principal" />
                  <ChatBubble c={c} font={font} side="me" text="¡Hola Carlos! Tenemos varios modelos. ¿Qué medida necesitas?" />
                  <ChatBubble c={c} font={font} side="them" text="Como 4 pulgadas, color bronce" />
                  <ChatBubble c={c} font={font} side="me" text="Perfecto, tenemos la línea Yale en stock. Te paso fotos 📸" />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div>
              <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Botones</p>
              <div className="flex flex-wrap gap-3">
                <button style={{ background: c.primary, color: '#fff' }} className="px-5 py-2.5 rounded-[10px] text-sm font-bold hover:opacity-90 transition" data-testid="btn-primary">
                  Acción principal
                </button>
                <button style={{ background: c.surface, color: c.text, borderColor: c.border }} className="border px-5 py-2.5 rounded-[10px] text-sm font-bold hover:opacity-80 transition" data-testid="btn-secondary">
                  Secundario
                </button>
                <button style={{ background: c.success, color: '#fff' }} className="px-5 py-2.5 rounded-[10px] text-sm font-bold flex items-center gap-2" data-testid="btn-success">
                  <Check size={16} weight="bold" /> Confirmar venta
                </button>
                <button style={{ background: `${c.primary}15`, color: c.primary }} className="px-5 py-2.5 rounded-[10px] text-sm font-bold" data-testid="btn-ghost">
                  Botón fantasma
                </button>
              </div>
            </div>

            {/* Avatars with initials */}
            <div>
              <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Avatares con iniciales</p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { name: 'Fabiola', color: '#C5533B' },
                  { name: 'Jackson', color: '#4A7C59' },
                  { name: 'Edinson', color: '#0EA5E9' },
                  { name: 'Julio', color: '#F2A900' },
                  { name: 'Randy', color: '#A855F7' },
                  { name: 'Wilder', color: '#E27D60' },
                ].map((u) => (
                  <div key={u.name} className="flex items-center gap-2">
                    <div
                      style={{ background: u.color }}
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm shadow-sm"
                    >
                      {u.name.substring(0, 2).toUpperCase()}
                    </div>
                    <span style={{ color: c.text }} className="text-xs font-bold">{u.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Typography sample */}
            <div style={{ background: c.surface, borderColor: c.border }} className="border rounded-[16px] p-5">
              <p style={{ color: c.textMuted }} className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Tipografía — {font.name}</p>
              <h1 style={{ fontFamily: font.heading, fontWeight: 800, color: c.text }} className="text-4xl tracking-tight">Q 187,450</h1>
              <h2 style={{ fontFamily: font.heading, fontWeight: 700, color: c.text }} className="text-2xl tracking-tight mt-2">Mi Día — Vendedor del mes</h2>
              <p style={{ color: c.text }} className="text-base mt-2">Texto del cuerpo en Manrope. Ideal para tablas, mensajes y descripciones largas. Legible a cualquier tamaño.</p>
              <p style={{ color: c.textMuted }} className="text-xs mt-1 uppercase tracking-[0.2em] font-bold">Etiqueta pequeña en negrita</p>
            </div>
          </div>
        </section>

        <p style={{ color: c.textMuted }} className="text-center text-xs pt-8">
          Cuando decidas qué combinación te gusta más, dime: <strong style={{ color: c.text }}>"Paleta X + Modo Y + Fuente Z"</strong>
        </p>
      </div>
    </div>
  );
};

const SidebarGroup = ({ label, c, font, children }) => (
  <div className="mb-4">
    <p style={{ color: c.textMuted, fontFamily: font.body }} className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1 px-2">{label}</p>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const NavItem = ({ icon: Icon, label, c, badge, active }) => (
  <div
    style={{
      background: active ? `${c.primary}15` : 'transparent',
      color: active ? c.primary : c.text,
      borderRightColor: active ? c.primary : 'transparent',
      borderRightWidth: active ? 3 : 0,
    }}
    className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-sm transition-colors hover:bg-black/[0.03] dark:hover:bg-white/5"
  >
    <Icon size={18} weight={active ? 'fill' : 'regular'} />
    <span className={active ? 'font-bold' : 'font-medium'}>{label}</span>
    {badge && (
      <span style={{ background: active ? c.primary : c.surfaceMuted, color: active ? '#fff' : c.text }} className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full">
        {badge}
      </span>
    )}
  </div>
);

const KpiCard = ({ c, font, label, value, trend, trendUp, accent }) => (
  <div
    style={{
      background: accent ? `linear-gradient(135deg, ${c.surface}, ${c.primary}08)` : c.surface,
      borderColor: c.border,
    }}
    className="border rounded-[16px] p-5 hover:shadow-md transition-shadow"
    data-testid="kpi-card"
  >
    <p style={{ color: c.textMuted }} className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1">{label}</p>
    <p style={{ fontFamily: font.heading, fontWeight: 800, color: c.text }} className="text-3xl tracking-tight">{value}</p>
    {trend && (
      <span style={{ color: trendUp ? c.success : c.warning, background: `${trendUp ? c.success : c.warning}15` }} className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full">
        {trendUp ? '↑' : '↓'} {trend}
      </span>
    )}
  </div>
);

const QuickAction = ({ c, font, icon: Icon, label, count, color, highlight }) => (
  <button
    style={{
      background: `${color}12`,
      borderColor: `${color}40`,
      color,
      boxShadow: highlight ? `0 0 0 3px ${color}25` : 'none',
    }}
    className="border-2 rounded-[14px] p-4 text-left hover:scale-[1.02] active:scale-95 transition-transform"
    data-testid={`quick-${label.toLowerCase()}`}
  >
    <div className="flex items-center justify-between">
      <Icon size={26} weight="fill" />
      <span className="text-[9px] uppercase tracking-wide font-bold opacity-80">+ Nuevo</span>
    </div>
    <p style={{ fontFamily: font.heading, fontWeight: 700 }} className="mt-2 text-lg">{label}</p>
    <p className="text-[10px] uppercase tracking-wide opacity-70 font-bold">Hoy: {count}</p>
  </button>
);

const ChatHeader = ({ c, font }) => (
  <div style={{ borderColor: c.border }} className="border-b pb-3 flex items-center gap-3">
    <div style={{ background: c.primary, color: '#fff' }} className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm">CP</div>
    <div className="flex-1">
      <p style={{ fontFamily: font.heading, color: c.text }} className="text-sm font-bold">Carlos Pérez</p>
      <p style={{ color: c.success }} className="text-[10px] font-bold flex items-center gap-1">
        <WhatsappLogo size={12} weight="fill" /> WhatsApp · +502 5430 7807
      </p>
    </div>
    <Bell size={18} color={c.textMuted} />
  </div>
);

const ChatBubble = ({ c, font, side, text }) => {
  const me = side === 'me';
  return (
    <div className={`flex ${me ? 'justify-end' : 'justify-start'}`}>
      <div
        style={{
          background: me ? c.primary : c.surfaceMuted,
          color: me ? '#fff' : c.text,
          borderTopRightRadius: me ? 4 : 16,
          borderTopLeftRadius: me ? 16 : 4,
          fontFamily: font.body,
        }}
        className="px-4 py-2 rounded-2xl max-w-[75%] text-sm leading-relaxed"
      >
        {text}
      </div>
    </div>
  );
};

export default DesignPreview;
